import { RefObject, useEffect, useLayoutEffect, useRef } from "react";

// How close to the bottom (px) still counts as "following": tolerates
// sub-pixel rounding and the last line's descent.
export const STICK_TO_BOTTOM_THRESHOLD_PX = 24;

export const isAtBottom = (scroller: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}): boolean =>
  scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <=
  STICK_TO_BOTTOM_THRESHOLD_PX;

// Keeps a chat scroller pinned to its bottom the way a chat expects, in
// natural DOM order (no column-reverse, so reading and tab order stay
// chronological):
// - Position is set in a layout effect after EVERY commit while following,
//   i.e. before paint: a freshly hydrated or switched chat paints already
//   scrolled down — never a top-then-bottom motion.
// - Scrolling away from the bottom stops following (incoming events do not
//   yank the reader); scrolling back to the bottom resumes it.
// - A `resetKey` change (another chat selected) starts following again.
// - Late layout shifts that happen without a React commit (a table settling,
//   citation cards loading) are followed via ResizeObserver while pinned.
export const useStickToBottom = ({
  scrollerRef,
  contentRef,
  resetKey,
}: {
  scrollerRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  resetKey: unknown;
}): void => {
  const pinnedRef = useRef(true);
  const lastResetKeyRef = useRef(resetKey);
  if (lastResetKeyRef.current !== resetKey) {
    lastResetKeyRef.current = resetKey;
    pinnedRef.current = true;
  }

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller && pinnedRef.current)
      scroller.scrollTop = scroller.scrollHeight;
  });

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onScroll = () => {
      pinnedRef.current = isAtBottom(scroller);
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [scrollerRef]);

  useEffect(() => {
    const content = contentRef.current;
    const scroller = scrollerRef.current;
    if (!content || !scroller || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) scroller.scrollTop = scroller.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [contentRef, scrollerRef]);
};
